import { useCustom, useTranslate } from '@refinedev/core';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  Radio,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd';
import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { useMemo, useState } from 'react';
import { SeriesChart } from '../../components/charts/SeriesChart';
import {
  CHECK_IN_DRIVER_LABEL_KEYS,
  DRIVERS,
  MOODS,
  TREND_FIELDS,
  TREND_WINDOWS,
  emotionLabel,
  emotionOptions,
  moodSeriesOf,
  newCheckInId,
  recordableDays,
  sessionInputOf,
  type CheckInEntryRow,
  type SessionRecordDraft,
  type TrendRow,
  type TrendWindow,
} from './model';
import { useCheckInCommand } from './useCheckInCommand';
// E43 — the phone's sleep and active minutes under the mood line (Q-e43g1-6 A)
import { CheckInPhoneRows } from '../health/CheckInPhoneRows';

/**
 * E37 · ClientCheckInTrend (buildOrder 6) + RecordCheckInInSession (8) + DeleteCheckIn (hub, 9) — "Client Hub —
 * Check-ins (trend)": the client hub's Check-ins tab. Mood over 14 / 30 / 90 days (a line, one point a day — the
 * day's lowest — with gaps where nobody checked in), how often the client checked in, and the entries with their
 * notes and drivers. Visible through a granted relation (E07), like every hub tab.
 *
 * IN SESSION: the therapist can Record a check-in (telehealth with a shared screen, a client with no phone): mood
 * 1–5 is the one required line; the optional lines are the phone's; today or up to 7 days back; no dictation. It is
 * the practice's record, marked "Entered in session", and never counts as the client checking in. An entry this
 * practice entered offers Remove; the client's own entries and other practices' are never offered it (the query
 * returns only this practice's in-session entries). Asking for a DAILY check-in is E34's Assign drawer (kind tool →
 * Daily check-in): the client's own check-in completes that homework.
 *
 * E43 — the phone's sleep and active minutes appear under the mood line for the types the client shares with THIS
 * therapist ("not shared" otherwise): `../health/CheckInPhoneRows.tsx`.
 */
type Props = {
  clientId: string;
  clientName: string;
  locale: 'en' | 'ru';
};

const emptyDraft = (recordedFor: string): SessionRecordDraft => ({
  mood: null,
  recordedFor,
  energy: null,
  sleepQuality: null,
  sleepHours: null,
  socialConnection: null,
  feelings: [],
  drivers: [],
  note: '',
});

export const ClientCheckInsTab = ({ clientId, clientName, locale }: Props) => {
  const t = useTranslate();
  const [days, setDays] = useState<TrendWindow>(14);
  const [recording, setRecording] = useState(false);
  const [draft, setDraft] = useState<SessionRecordDraft>(() =>
    emptyDraft(recordableDays(new Date())[0]),
  );
  const [message, setMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const [recordId, setRecordId] = useState(() => newCheckInId());
  const { run, isLoading } = useCheckInCommand();
  const dateLocale = locale === 'ru' ? ru : enUS;

  const { data, isFetching, isError, refetch } = useCustom<TrendRow>({
    url: '',
    method: 'get',
    errorNotification: false,
    meta: {
      operation: 'clientCheckInTrend',
      fields: TREND_FIELDS,
      variables: {
        subject: { name: 'subject', type: 'ID!', value: clientId },
        days: { name: 'days', type: 'Int', value: days },
      },
    },
    queryOptions: { enabled: Boolean(clientId), retry: false },
  });
  const trend = data?.data;
  const entries = useMemo(() => trend?.entries ?? [], [trend]);
  const series = useMemo(() => moodSeriesOf(entries), [entries]);
  const options = useMemo(() => emotionOptions(locale), [locale]);

  const dayLabel = (day: string) =>
    format(parseISO(day), 'd MMM yyyy', { locale: dateLocale });

  const onRecord = async () => {
    const input = sessionInputOf(clientId, recordId, draft);
    if (!input) return;
    const res = await run(
      'recordCheckInInSession',
      input,
      t('checkIn.hub.loadError'),
    );
    if (res.ok) {
      setMessage({ type: 'success', text: t('checkIn.session.saved') });
      setRecording(false);
      setDraft(emptyDraft(recordableDays(new Date())[0]));
      setRecordId(newCheckInId());
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  const onRemove = async (checkInId: string) => {
    const res = await run(
      'deleteCheckIn',
      { checkInId, subject: clientId },
      t('checkIn.session.removeFailed'),
    );
    if (res.ok) {
      setMessage(null);
      refetch();
    } else {
      setMessage({ type: 'error', text: res.message });
    }
  };

  const columns = [
    {
      title: t('checkIn.session.day'),
      width: 130,
      render: (_: unknown, r: CheckInEntryRow) => dayLabel(r.recordedFor),
    },
    {
      title: t('checkIn.hub.mood'),
      width: 150,
      render: (_: unknown, r: CheckInEntryRow) => (
        <Space direction="vertical" size={0}>
          <Tag data-testid={`checkin-hub-mood-${r.checkInId}`}>
            {r.mood} · {t(`checkIn.mood.${r.mood}`)}
          </Tag>
          {r.enteredInSession ? (
            <Tag
              color="blue"
              data-testid={`checkin-hub-in-session-${r.checkInId}`}
            >
              {t('checkIn.hub.enteredInSession')}
            </Tag>
          ) : null}
        </Space>
      ),
    },
    {
      title: t('checkIn.hub.feelings'),
      render: (_: unknown, r: CheckInEntryRow) =>
        (r.emotions ?? []).map((e) => emotionLabel(e.id, locale)).join(' · ') ||
        '—',
    },
    {
      title: t('checkIn.hub.drivers'),
      render: (_: unknown, r: CheckInEntryRow) =>
        (r.drivers ?? []).map((d) => t(`checkIn.driver.${d}`)).join(' · ') ||
        '—',
    },
    {
      title: t('checkIn.hub.note'),
      render: (_: unknown, r: CheckInEntryRow) => (
        <Space direction="vertical" size={0}>
          {r.energy ? (
            <span>
              {t('checkIn.energy')}: {r.energy}
            </span>
          ) : null}
          {r.sleepQuality ? (
            <span>
              {t('checkIn.hub.sleepQuality')}: {r.sleepQuality}
            </span>
          ) : null}
          {r.sleepHours ? (
            <span>
              {t('checkIn.hub.sleepHours')}: {r.sleepHours}
            </span>
          ) : null}
          {r.socialConnection ? (
            <span>
              {t('checkIn.hub.socialConnection')}: {r.socialConnection}
            </span>
          ) : null}
          {r.note ? (
            <span data-testid={`checkin-hub-note-${r.checkInId}`}>
              {r.note}
            </span>
          ) : null}
        </Space>
      ),
    },
    {
      title: '',
      width: 110,
      render: (_: unknown, r: CheckInEntryRow) =>
        r.enteredInSession ? (
          <Popconfirm
            cancelText={t('checkIn.session.removeCancel')}
            okText={t('checkIn.session.removeConfirm')}
            onConfirm={() => onRemove(r.checkInId)}
            title={t('checkIn.session.removeTitle')}
            description={t('checkIn.session.removeBody')}
          >
            <Button
              danger
              size="small"
              data-testid={`checkin-hub-remove-${r.checkInId}`}
            >
              {t('checkIn.session.remove')}
            </Button>
          </Popconfirm>
        ) : null,
    },
  ];

  const set = (patch: Partial<SessionRecordDraft>) =>
    setDraft((d) => ({ ...d, ...patch }));
  const scale = (
    key: 'energy' | 'sleepQuality' | 'socialConnection',
    label: string,
  ) => (
    <Form.Item label={label}>
      <Select
        allowClear
        data-testid={`checkin-session-${key}`}
        onChange={(v) => set({ [key]: v ?? null })}
        options={[1, 2, 3, 4, 5].map((v) => ({
          value: v,
          label: `${v} · ${t(`checkIn.scale.${v}`)}`,
        }))}
        style={{ width: 200 }}
        value={draft[key] ?? undefined}
      />
    </Form.Item>
  );

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%' }}
      data-testid="checkin-hub-tab"
    >
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('checkIn.hub.title')}
          </Typography.Title>
          <Typography.Text
            type="secondary"
            data-testid="checkin-hub-days-checked"
          >
            {trend
              ? t('checkIn.hub.daysChecked', {
                  count: trend.daysCheckedIn,
                  days,
                })
              : ''}
          </Typography.Text>
        </div>
        <Space wrap>
          <Segmented
            data-testid="checkin-hub-range"
            onChange={(v) => setDays(v as TrendWindow)}
            options={TREND_WINDOWS.map((d) => ({
              value: d,
              label: t(`checkIn.hub.range${d}`),
            }))}
            value={days}
          />
          <Button
            data-testid="checkin-hub-record"
            onClick={() => {
              setRecording((r) => !r);
              setMessage(null);
            }}
            type="primary"
          >
            + {t('checkIn.session.record')}
          </Button>
        </Space>
      </Space>

      <Typography.Text type="secondary">
        {t('checkIn.hub.askForDaily')}
      </Typography.Text>

      {message ? (
        <Alert
          type={message.type}
          showIcon
          message={message.text}
          data-testid={`checkin-hub-${message.type}`}
        />
      ) : null}
      {isError ? (
        <Alert type="error" showIcon message={t('checkIn.hub.loadError')} />
      ) : null}

      {recording ? (
        <Card
          title={t('checkIn.session.recordTitle', { name: clientName })}
          data-testid="checkin-session-form"
        >
          <Typography.Paragraph type="secondary">
            {t('checkIn.session.recordHint')}
          </Typography.Paragraph>
          <Form layout="vertical">
            <Form.Item label={t('checkIn.session.day')}>
              <Select
                data-testid="checkin-session-day"
                onChange={(v) => set({ recordedFor: v })}
                options={recordableDays(new Date()).map((d) => ({
                  value: d,
                  label: dayLabel(d),
                }))}
                style={{ width: 200 }}
                value={draft.recordedFor}
              />
            </Form.Item>
            <Form.Item label={t('checkIn.title')} required>
              <Radio.Group
                buttonStyle="solid"
                onChange={(e) => set({ mood: e.target.value })}
                value={draft.mood}
              >
                {MOODS.map((m) => (
                  <Radio.Button
                    key={m}
                    value={m}
                    data-testid={`checkin-session-mood-${m}`}
                  >
                    {m} · {t(`checkIn.mood.${m}`)}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
            {scale('energy', t('checkIn.energy'))}
            {scale('sleepQuality', t('checkIn.sleepQuality'))}
            <Form.Item label={t('checkIn.sleepHours')}>
              <InputNumber
                data-testid="checkin-session-sleep-hours"
                max={24}
                min={0}
                onChange={(v) =>
                  set({ sleepHours: v === null ? null : Number(v) })
                }
                step={0.5}
                value={draft.sleepHours}
              />
            </Form.Item>
            {scale('socialConnection', t('checkIn.socialConnection'))}
            <Form.Item label={t('checkIn.emotions')}>
              <Select
                data-testid="checkin-session-feelings"
                mode="multiple"
                onChange={(v) => set({ feelings: v })}
                optionFilterProp="label"
                options={options}
                value={draft.feelings}
              />
            </Form.Item>
            <Form.Item label={t('checkIn.drivers')}>
              <Select
                data-testid="checkin-session-drivers"
                mode="multiple"
                onChange={(v) => set({ drivers: v })}
                options={DRIVERS.map((d) => ({
                  value: d,
                  label: t(CHECK_IN_DRIVER_LABEL_KEYS[d]),
                }))}
                value={draft.drivers}
              />
            </Form.Item>
            <Form.Item label={t('checkIn.note')}>
              <Input.TextArea
                data-testid="checkin-session-note"
                maxLength={4000}
                onChange={(e) => set({ note: e.target.value })}
                rows={3}
                value={draft.note}
              />
            </Form.Item>
            <Button
              data-testid="checkin-session-save"
              disabled={draft.mood === null}
              loading={isLoading}
              onClick={onRecord}
              type="primary"
            >
              {t('checkIn.session.save')}
            </Button>
          </Form>
        </Card>
      ) : null}

      {entries.length === 0 && !isFetching && !isError ? (
        <Typography.Paragraph type="secondary" data-testid="checkin-hub-empty">
          {t('checkIn.hub.empty')}
        </Typography.Paragraph>
      ) : (
        <>
          <SeriesChart
            ariaLabel={t('checkIn.hub.mood')}
            locale={locale}
            series={series.map((s) => ({ ...s, label: t('checkIn.hub.mood') }))}
            testId="checkin-hub-chart"
            yRange={[1, 5]}
          />
          {/* E43 — the phone's two rows under the mood line */}
          {trend ? <CheckInPhoneRows phone={trend} locale={locale} /> : null}
          <Table
            columns={columns}
            dataSource={entries}
            loading={isFetching}
            pagination={false}
            rowKey="checkInId"
            data-testid="checkin-hub-table"
          />
        </>
      )}
    </Space>
  );
};
